import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import {
  useOrderKnowledgeBaseArticlesMutation,
  useOrderKnowledgeBaseCategoriesMutation,
  useGetKnowledgeBaseCategoriesQuery,
  useGetKnowledgeBaseIndexQuery,
  useGetKnowledgeBaseLibrariesQuery,
  type KnowledgeBaseCategory,
  type KnowledgeBaseLibraryEntry,
} from "knowledge-base/_api/knowledge-base-api";
import type { KnowledgeBaseLibrary } from "knowledge-base/_schemas/knowledge-base-article-schema";
import type {
  OrderArticlesIntent,
  OrderCategoriesIntent,
} from "knowledge-base/articles/_views/article-filing";
import ArticleCreatorDialog from "knowledge-base/articles/_views/article-creator-dialog";
import ArticleEditorPane from "knowledge-base/articles/_views/article-editor-pane";
import ArticleReader from "knowledge-base/articles/_views/article-reader";
import {
  loadContentsCollapsed,
  saveContentsCollapsed,
  wasChosenFromContents,
} from "knowledge-base/_views/knowledge-base-contents-preference";
import KnowledgeBaseIndexTree from "knowledge-base/_views/knowledge-base-index-tree";
import KnowledgeBaseSearch from "knowledge-base/_views/knowledge-base-search";
import CategoryEditorDialog from "knowledge-base/categories/_views/category-editor-dialog";
import ClientLibraryShelf from "knowledge-base/libraries/_views/client-library-shelf";
import { useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";
import { shellBottomBarHeight } from "_shared/_views/shell-bottom-bar";

type Props = Readonly<{
  /**
   * Pins the page to Attorney Hub's public help library, for the route under Support. Without it
   * the page shows the reader's own organization and whichever client libraries they can open.
   */
  publicLibraryOnly?: boolean;
}>;

/**
 * A knowledge base: a contents tree on the left, an article on the right.
 *
 * Which knowledge base is a real choice here, not a fixed thing. Every organization has two of its
 * own — internal rules for its own people, external rules for the firms working for it — and a firm
 * doing work for several clients can also open each of those clients' external libraries. The
 * switcher is the only way to tell them apart, so it sits above the contents rather than inside a
 * menu.
 */
export default function KnowledgeBasePage(props: Props) {
  // Read rather than destructured with a default: a default inside a destructuring pattern makes
  // the React Compiler bail out of this whole component (see app/CLAUDE.md).
  const publicLibraryOnly = props.publicLibraryOnly ?? false;

  const { slug } = useParams<{ slug?: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEditing = location.pathname.endsWith("/edit");

  const { data: libraries, isLoading: loadingLibraries } = useGetKnowledgeBaseLibrariesQuery();

  const selectable = selectableLibraries(libraries, publicLibraryOnly);
  const selected = selectedLibrary(selectable, searchParams.get("owner"), searchParams.get("library"));

  const {
    data: entries,
    isLoading: loadingEntries,
    isError: entriesFailed,
  } = useGetKnowledgeBaseIndexQuery(
    selected
      ? {
          library: selected.library,
          ownerLegalEntityId: selected.ownerLegalEntityId,
          includeDrafts: true,
        }
      : { includeDrafts: true }
  );

  const { data: categories } = useGetKnowledgeBaseCategoriesQuery(
    selected ? selected.library : undefined
  );

  // Which way the panel folds, and which way round the chevron points, is the one thing the
  // layout's own breakpoints cannot express: Collapse takes its orientation as a prop. Asked at
  // exactly the breakpoint the page stacks at, so the two can never disagree.
  const theme = useTheme();
  const stacked = useMediaQuery(theme.breakpoints.down("md"));

  const [contentsCollapsed, setContentsCollapsed] = useState(loadContentsCollapsed);

  // Stacked, an article chosen from the contents folds them for that visit, so the article is the
  // next thing on screen rather than somewhere below the tree. Keyed to the navigation, so a
  // reader who opens the contents again keeps them open until they choose the next article.
  const [contentsReopenedFor, setContentsReopenedFor] = useState<string | null>(null);
  const foldedForChoice =
    stacked && wasChosenFromContents(location.state) && contentsReopenedFor !== location.key;
  const collapsed = contentsCollapsed || foldedForChoice;

  // Folded away beside the article rather than above it: the one state where the heading has a
  // column to live in rather than a row.
  const railed = collapsed && !stacked;

  const [editingCategory, setEditingCategory] = useState<KnowledgeBaseCategory | null>(null);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [creatingArticle, setCreatingArticle] = useState(false);
  const [filingFailure, setFilingFailure] = useState<string | null>(null);

  const [orderArticles] = useOrderKnowledgeBaseArticlesMutation();
  const [orderCategories] = useOrderKnowledgeBaseCategoriesMutation();

  const visibleEntries = entries?.filter(
    (entry) => !selected || entry.ownerLegalEntityId === selected.ownerLegalEntityId
  );
  const visibleCategories = categories?.filter(
    (category) => !selected || category.ownerLegalEntityId === selected.ownerLegalEntityId
  );

  const toggleContents = () => {
    if (collapsed) {
      // A fold made by choosing an article was never saved, so reopening only clears the saved
      // preference when there is one to clear.
      setContentsReopenedFor(location.key);
      if (contentsCollapsed) {
        setContentsCollapsed(false);
        saveContentsCollapsed(false);
      }
      return;
    }

    setContentsCollapsed(true);
    saveContentsCollapsed(true);
  };

  const chooseLibrary = (choice: KnowledgeBaseLibraryEntry) => {
    setSearchParams({ library: choice.library, owner: choice.ownerLegalEntityId });
  };

  const fileDroppedArticle = async (intent: OrderArticlesIntent) => {
    setFilingFailure(null);
    const outcome = await orderArticles(intent);

    if ("error" in outcome) {
      // The tree is driven by the server's answer, so a refusal leaves the article visibly where
      // it was. The reader still needs to be told, or the drag looks like it simply did not take.
      setFilingFailure(filingMessageFor(outcome.error));
    }
  };

  const reorderCategories = async (intent: OrderCategoriesIntent) => {
    setFilingFailure(null);
    const outcome = await orderCategories(intent);

    if ("error" in outcome) {
      // The usual refusals here explain themselves - "categories cannot nest more than 3 deep",
      // "a category named 'Billing' already exists here" - and are worth showing verbatim.
      setFilingFailure(filingMessageFor(outcome.error));
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 3,
        height: "100%",
        p: { xs: 2, md: 3 },
      }}
    >
      {/*
        A heading that is always there and everything else folded behind it. Collapsed, the panel
        keeps only that heading: a rail down the left beside the article on a wide screen, a bar
        across the top of it once the two stack.
      */}
      <Paper
        elevation={1}
        sx={{
          alignSelf: "flex-start",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          maxWidth: "100%",
          p: 2,
          // Wide by what it holds rather than by a number, so folding the contents away actually
          // narrows the panel. The width the panel has always had is stated on the contents
          // themselves, below.
          width: { xs: "100%", md: "auto" },
        }}
      >
        {/*
          Upright when the panel is a rail, so the rail stays a rail — a row there would hold the
          heading open at the width of an icon, a word and a button side by side.
        */}
        <Stack
          direction={railed ? "column" : "row"}
          spacing={1}
          sx={{ alignItems: "center", flexShrink: 0 }}
        >
          <MenuBookIcon color="primary" />
          {/*
            "Contents", not the entity's name. The page heading beside it already says whose
            knowledge base this is, and two headings saying the same thing is what made landing
            here read as though the page had been titled twice.

            The same heading on the rail, turned on its side: sideways it costs the width of one
            line of text rather than the width of the word, which is the only reason a rail can
            carry a label at all. Icon, heading, button in that order whichever way the panel is
            facing, so the rail reads as the header rotated rather than as a different thing.
          */}
          <Typography
            component="h2"
            sx={
              railed
                ? { my: 1, writingMode: "vertical-rl" }
                : { flexGrow: 1 }
            }
            variant={railed ? "subtitle2" : "h6"}
          >
            Contents
          </Typography>
          <Tooltip title={contentsToggleLabel(collapsed)}>
            <IconButton
              aria-controls={CONTENTS_REGION_ID}
              aria-expanded={!collapsed}
              aria-label={contentsToggleLabel(collapsed)}
              onClick={toggleContents}
              size="small"
            >
              {contentsToggleIcon(collapsed, stacked)}
            </IconButton>
          </Tooltip>
        </Stack>

        {/*
          Taken out of the page once it has folded away, rather than left behind at zero width. A
          horizontal collapse animates width alone and leaves height where the contents put it, so
          a panel folded away beside a long tree left a rail as tall as the tree had been. It also
          keeps a search box and a list of articles nobody can see out of the tab order.
        */}
        <Collapse
          id={CONTENTS_REGION_ID}
          in={!collapsed}
          orientation={stacked ? "vertical" : "horizontal"}
          unmountOnExit
        >
          {/*
            A column of three: a header that stays put, contents that scroll, and the New category
            button pinned under them. Scrolling the whole panel took the search box and that button
            out of reach exactly when the tree was long enough to need them.

            The width lives here rather than on the panel because a horizontal collapse animates
            the panel's width down to nothing, and content sized as a share of that would reflow
            all the way down with it.
          */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxHeight: { md: `calc(100vh - 220px - ${shellTopBannersHeight()} - ${shellBottomBarHeight()})` },
              pt: 2,
              width: { xs: "100%", md: 308 },
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              {!publicLibraryOnly && selected && (
                <LibrarySwitcher
                  onChoose={chooseLibrary}
                  selected={selected}
                  libraries={selectable}
                />
              )}

              {/*
                Pinned to the public library on Attorney Hub's page. Search otherwise crosses every
                library the reader may see, which on a help page meant their own organization's
                internal articles appearing inside Attorney Hub's knowledge base.
              */}
              <KnowledgeBaseSearch library={publicLibraryOnly ? "AttorneyHubPublic" : undefined} />

              <Divider sx={{ my: 2 }} />
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto" }}>
              {(loadingLibraries || loadingEntries) && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {entriesFailed && <Alert severity="error">The contents could not be loaded.</Alert>}

              {filingFailure && (
                <Alert onClose={() => setFilingFailure(null)} severity="error" sx={{ mb: 1 }}>
                  {filingFailure}
                </Alert>
              )}

              {visibleEntries && (
                <KnowledgeBaseIndexTree
                  activeSlug={slug}
                  categories={visibleCategories ?? []}
                  entries={visibleEntries}
                  onEditCategory={selected?.canAuthor ? setEditingCategory : undefined}
                  onFileArticle={
                    selected?.canAuthor ? (intent) => void fileDroppedArticle(intent) : undefined
                  }
                  onOrderCategories={
                    selected?.canAuthor ? (intent) => void reorderCategories(intent) : undefined
                  }
                />
              )}
            </Box>

            {selected?.canAuthor && (
              <Stack spacing={0.5} sx={{ flexShrink: 0, mt: 2 }}>
                <Button
                  fullWidth
                  onClick={() => setCreatingArticle(true)}
                  size="small"
                  startIcon={<AddIcon />}
                  variant="outlined"
                >
                  New article
                </Button>
                <Button
                  fullWidth
                  onClick={() => setCreatingCategory(true)}
                  size="small"
                  startIcon={<AddIcon />}
                >
                  New category
                </Button>
              </Stack>
            )}
          </Box>
        </Collapse>
      </Paper>

      <Box sx={{ flexGrow: 1, minWidth: 0, overflow: "auto" }}>
        {!slug && <KnowledgeBaseWelcome libraries={selectable} library={selected} />}
        {slug && !isEditing && (
          <ArticleReader
            canAuthor={selected?.canAuthor}
            ownerLegalEntityId={selected?.ownerLegalEntityId}
            slug={slug}
          />
        )}
        {slug && isEditing && (
          <ArticleEditorPane ownerLegalEntityId={selected?.ownerLegalEntityId} slug={slug} />
        )}
      </Box>

      {selected && creatingArticle && (
        <ArticleCreatorDialog
          categories={visibleCategories ?? []}
          library={selected.library}
          onClose={() => setCreatingArticle(false)}
          open
        />
      )}

      {selected && (creatingCategory || editingCategory) && (
        <CategoryEditorDialog
          category={editingCategory ?? undefined}
          heldItems={
            editingCategory
              ? itemsFiledIn(editingCategory.id, visibleEntries, visibleCategories)
              : undefined
          }
          library={selected.library}
          onClose={() => {
            setCreatingCategory(false);
            setEditingCategory(null);
          }}
          open
        />
      )}
    </Box>
  );
}

/**
 * The folded contents, named so the heading's toggle can point at them.
 *
 * One page, one contents panel, so a fixed id is safe and keeps the tests honest about the
 * relationship being declared at all.
 */
const CONTENTS_REGION_ID = "knowledge-base-contents";

function contentsToggleLabel(collapsed: boolean): string {
  return collapsed ? "Show contents" : "Hide contents";
}

/**
 * Which way the panel goes, drawn rather than described.
 *
 * Chevrons sideways where it folds away to the left of the article, and up and down where it
 * folds away above it — an arrow that pointed left on a panel that slides upwards would be
 * telling the reader the wrong thing about what the button does.
 */
function contentsToggleIcon(collapsed: boolean, stacked: boolean) {
  if (stacked) return collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />;

  return collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />;
}

type SwitcherProps = Readonly<{
  libraries: readonly KnowledgeBaseLibraryEntry[];
  selected: KnowledgeBaseLibraryEntry;
  onChoose: (choice: KnowledgeBaseLibraryEntry) => void;
}>;

/**
 * Internal and External as tabs, and — for a firm that works for other organizations — a picker for
 * whose external library to read.
 *
 * Tabs for the reader's own two because that pair is always present and switching between them is
 * the common move. A select for clients because how many there are depends on how much work the
 * firm has, and a row of tabs that grows with the client list would not survive a busy firm.
 */
function LibrarySwitcher({ libraries, selected, onChoose }: SwitcherProps) {
  const own = libraries.filter((entry) => entry.isOwn);
  const clients = libraries.filter((entry) => !entry.isOwn);

  const readingOwn = selected.isOwn;

  return (
    // Padding, not margin, for the clearance above: the picker's label is always shrunk, so it
    // floats above the field's box and sits nearer the heading than the layout's gap suggests —
    // and a top margin here would simply collapse into the heading's bottom margin and change
    // nothing. On the stack rather than the field because Stack zeroes its children's margins.
    <Stack spacing={1.5} sx={{ mb: 2, pt: 0.5 }}>
      {clients.length > 0 && (
        <TextField
          label="Knowledge bases you can read"
          onChange={(event) => {
            const choice = libraryPickedIn(libraries, event.target.value);
            if (choice) onChoose(choice);
          }}
          select
          size="small"
          // Reading your own knowledge base is a state this picker has to be able to show, not the
          // absence of one. Without these the empty value renders a blank box with the label still
          // sitting inside it, so the way back looked like a control nobody had filled in.
          slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
          value={readingOwn ? "" : selected.ownerLegalEntityId}
        >
          <MenuItem value="">
            <em>Your own knowledge base</em>
          </MenuItem>
          {clients.map((entry) => (
            <MenuItem key={entry.ownerLegalEntityId} value={entry.ownerLegalEntityId}>
              {entry.ownerName}
            </MenuItem>
          ))}
        </TextField>
      )}

      {/*
        Below the picker and only while reading your own, because Internal and External are a
        division of *your* knowledge base. Above it they looked like a filter on the list of
        knowledge bases you can read, which they never were — nobody reads another organization's
        internal library, so there is nothing for them to do once a client's is open.
      */}
      {readingOwn && (
        <Tabs
          onChange={(_event, value: KnowledgeBaseLibrary) => {
            const choice = own.find((entry) => entry.library === value);
            if (choice) onChoose(choice);
          }}
          value={selected.library}
          variant="fullWidth"
        >
          {own.map((entry) => (
            <Tab
              key={entry.library}
              label={entry.library === "Internal" ? "Internal" : "External"}
              value={entry.library}
            />
          ))}
        </Tabs>
      )}
    </Stack>
  );
}

/**
 * Narrows the libraries the reader can open to the ones this page is for.
 */
export function selectableLibraries(
  libraries: readonly KnowledgeBaseLibraryEntry[] | undefined,
  publicLibraryOnly: boolean
): KnowledgeBaseLibraryEntry[] {
  if (!libraries) return [];

  return publicLibraryOnly
    ? libraries.filter((entry) => entry.library === "AttorneyHubPublic")
    : libraries.filter((entry) => entry.library !== "AttorneyHubPublic");
}

/**
 * Which library the picker's value names.
 *
 * The empty option is "your own knowledge base", and it has to resolve to a real library rather
 * than to nothing. Matching the empty value against the client list finds no owner with an empty
 * id, so choosing it was silently inert: the reader asked to go back to their own knowledge base
 * and stayed in the client's.
 *
 * Internal for the way back, because it is the library the page opens on and the tab that will
 * light up beside the picker. Falls back to whichever own library exists, so an organization with
 * only one of the two still gets somewhere.
 */
export function libraryPickedIn(
  libraries: readonly KnowledgeBaseLibraryEntry[],
  ownerLegalEntityId: string
): KnowledgeBaseLibraryEntry | undefined {
  if (ownerLegalEntityId === "") {
    const own = libraries.filter((entry) => entry.isOwn);
    return own.find((entry) => entry.library === "Internal") ?? own[0];
  }

  return libraries.find(
    (entry) => !entry.isOwn && entry.ownerLegalEntityId === ownerLegalEntityId
  );
}

/**
 * Works out which library the reader is looking at.
 *
 * The address bar wins when it names one the reader can actually open — that is what makes a link
 * to a particular client's rules shareable. A stale or unauthorized pair in the URL falls back to
 * the first available library rather than showing an error, because the reader did not necessarily
 * type it: it may be a bookmark from before an agreement ended.
 */
export function selectedLibrary(
  selectable: readonly KnowledgeBaseLibraryEntry[],
  owner: string | null,
  library: string | null
): KnowledgeBaseLibraryEntry | undefined {
  const requested =
    owner && library
      ? selectable.find(
          (entry) => entry.ownerLegalEntityId === owner && entry.library === library
        )
      : undefined;

  return requested ?? selectable[0];
}

/**
 * What is filed directly in one category — articles and subcategories together.
 *
 * Directly, not recursively: the server refuses to remove a category that holds anything at all,
 * and a count that included grandchildren would name a number the reader cannot see in the tree.
 */
export function itemsFiledIn(
  categoryId: string,
  entries: readonly { categoryId?: string | null }[] | undefined,
  categories: readonly { parentCategoryId?: string | null }[] | undefined
): number {
  const articles = entries?.filter((entry) => entry.categoryId === categoryId).length ?? 0;
  const children =
    categories?.filter((category) => category.parentCategoryId === categoryId).length ?? 0;

  return articles + children;
}

/**
 * The server's own words where it gave any, and a plain sentence where it did not.
 *
 * Read through the shared error reader rather than off `error.data`: a refusal is the v1 Problem
 * Details contract now, not the bare string this used to expect, and reading the old shape is how
 * every knowledge base refusal silently became "could not be moved" (#4933). The reader only yields
 * text it trusts, so the fallback still covers a body that carries no reviewed prose.
 */
function filingMessageFor(error: unknown): string {
  return getErrorMessageFromUnknown(error) ?? "That could not be moved. Please try again.";
}

function KnowledgeBaseWelcome({
  libraries,
  library,
}: Readonly<{
  libraries: readonly KnowledgeBaseLibraryEntry[];
  library?: KnowledgeBaseLibraryEntry;
}>) {
  // The one heading that names the knowledge base, so it says which one. Falls back to the plain
  // words rather than "undefined Knowledge Base" while the library list is still loading.
  const heading = library ? `${library.ownerName} Knowledge Base` : "Knowledge Base";

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography component="h1" gutterBottom variant="h4">
        {heading}
      </Typography>
      {library && !library.isOwn && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read access to {library.ownerName}&apos;s knowledge base. Only{" "}
          {library.ownerName} can change what is in it.
        </Alert>
      )}
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Choose a page from the contents, or search for what you need.
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        You can also ask the AI assistant a question in your own words — it answers from these same
        pages, alongside the policies, fee schedules and agreements that apply to your work.
      </Typography>

      <ClientLibraryShelf libraries={libraries} reading={library} />
    </Box>
  );
}
